/**
 * Radio Station Playback Composable
 *
 * Handles non-interactive radio station playback with:
 * - Random start position
 * - Continuous looping
 * - Ad insertion (every 5 songs)
 * - Auto-skipping unavailable tracks
 * - Non-interactive mode (no seeking, no manual skipping)
 */

import { ref, computed, watch, onUnmounted, type ComputedRef } from "vue";
import { useAudioPlayer } from "./useAudioPlayer";
import type { Post } from "@/types";

interface RadioStationPlaybackSession {
  stationUlid: string;
  startPosition: number;
  currentPosition: number;
  tracks: Post[];
  startedAt: number;
  songsPlayed: number;
}

// Global state for radio station playback session
const radioSession = ref<RadioStationPlaybackSession | null>(null);
const isRadioMode = ref(false);

// Type for the radio station composable return value
type RadioStationComposable = {
  radioSession: ComputedRef<RadioStationPlaybackSession | null>;
  isRadioMode: ComputedRef<boolean>;
  startRadioStation: (stationUlid: string) => Promise<void>;
  stopRadioStation: () => void;
  skipUnavailableTrack: () => void;
};

// Export singleton instance
let radioStationInstance: RadioStationComposable | null = null;

export function useRadioStation(): RadioStationComposable {
  // Return singleton instance if already created
  if (radioStationInstance) {
    return radioStationInstance;
  }

  const audioPlayer = useAudioPlayer();

  /**
   * Start playing a radio station
   */
  async function startRadioStation(stationUlid: string) {
    console.log("[RadioStation] 🎵 Starting radio station:", stationUlid);
    try {
      // Fetch radio station with tracks - start position is now included in the response
      console.log("[RadioStation] 📥 Fetching radio station with tracks (includes start position)...");
      const { getSdk } = await import("@/composables/useSdk");
      const sdk = getSdk();
      const fullResponse = await sdk.getRadioStation(stationUlid, true);
      
      // Extract start position and total tracks from response (they're in the response object, not in data)
      const startPosition = (fullResponse as any).start_position ?? 0;
      const totalTracks = (fullResponse as any).total_tracks ?? 0;
      console.log("[RadioStation] ✅ Got start position from response:", { startPosition, totalTracks });
      
      const station = fullResponse.data;
      console.log("[RadioStation] ✅ Step 2 complete - Station fetched:", {
        ulid: station?.ulid,
        name: (station as any)?.name,
        hasTracks: !!(station as any)?.tracks,
        hasSongs: !!(station as any)?.songs,
        tracksType: typeof (station as any)?.tracks,
        tracksIsArray: Array.isArray((station as any)?.tracks),
      });

      // API returns tracks in 'tracks' field when includeTracks=true, or we need to fetch them separately
      // Check both 'tracks' and 'songs' for compatibility
      // Handle FeedResourceCollection format: tracks may be { data: [...] } or just [...]
      console.log("[RadioStation] 🔍 Step 3: Extracting tracks from station object...");
      let stationTracks = (station as any).tracks || (station as any).songs || [];
      console.log("[RadioStation] Raw tracks:", {
        fromTracks: !!(station as any).tracks,
        fromSongs: !!(station as any).songs,
        tracksType: typeof stationTracks,
        tracksIsArray: Array.isArray(stationTracks),
        hasDataKey: stationTracks && typeof stationTracks === 'object' && 'data' in stationTracks,
      });
      
      // Extract data array if wrapped in FeedResourceCollection format
      if (stationTracks && typeof stationTracks === 'object' && 'data' in stationTracks && Array.isArray(stationTracks.data)) {
        console.log("[RadioStation] 📦 Extracting tracks from FeedResourceCollection.data...");
        stationTracks = stationTracks.data;
        console.log("[RadioStation] Extracted tracks:", {
          count: stationTracks.length,
          isArray: Array.isArray(stationTracks),
        });
      }
      
      // Ensure stationTracks is an array
      if (!Array.isArray(stationTracks)) {
        console.error("[RadioStation] ❌ Invalid tracks format:", { 
          stationTracks, 
          type: typeof stationTracks,
          keys: stationTracks && typeof stationTracks === 'object' ? Object.keys(stationTracks) : [],
        });
        stationTracks = [];
      }
      
      console.log("[RadioStation] ✅ Step 3 complete - Tracks extracted:", {
        tracksCount: stationTracks.length,
        firstTrack: stationTracks[0] ? {
          ulid: stationTracks[0].ulid,
          hasTitle: !!stationTracks[0].title,
          hasBody: !!stationTracks[0].body,
          hasStreamUrl: !!stationTracks[0].streamUrl,
          hasAudio: !!stationTracks[0].audio,
          keys: Object.keys(stationTracks[0]).slice(0, 15),
        } : null,
      });
      
      // Wait for tracks to be fully hydrated if they're feed items (ULIDs only)
      // The SDK's fetchPostsBatch uses debouncing (100ms) + API call time, so wait a bit
      console.log("[RadioStation] 🔍 Step 4: Checking if tracks need hydration...");
      if (stationTracks.length > 0) {
        const firstTrack = stationTracks[0];
        const isFeedItem = firstTrack && !firstTrack.title && !firstTrack.body && !firstTrack.streamUrl && !firstTrack.audio && firstTrack.ulid;
        console.log("[RadioStation] Track analysis:", {
          isFeedItem,
          firstTrack: {
            ulid: firstTrack?.ulid,
            hasTitle: !!firstTrack?.title,
            hasBody: !!firstTrack?.body,
            hasStreamUrl: !!firstTrack?.streamUrl,
            hasAudio: !!firstTrack?.audio,
            keys: firstTrack ? Object.keys(firstTrack).slice(0, 10) : [],
          },
        });
        
        if (isFeedItem) {
          console.log("[RadioStation] ⏳ Tracks are feed items (ULIDs only), waiting for SDK hydration...");
          // The SDK should have already hydrated them, but give it a moment to complete
          // fetchPostsBatch has 100ms debounce + API call, so wait up to 600ms total
          let retries = 0;
          const maxRetries = 3;
          const delayMs = 200; // Wait 200ms between checks
          
          while (retries < maxRetries) {
            console.log(`[RadioStation] ⏱️  Waiting ${delayMs}ms before retry ${retries + 1}/${maxRetries}...`);
            await new Promise(resolve => setTimeout(resolve, delayMs));
            
            // Re-fetch to get the hydrated tracks from the SDK
            console.log(`[RadioStation] 🔄 Retry ${retries + 1}: Re-fetching station to check hydration...`);
            const refreshedResponse = await sdk.getRadioStation(stationUlid, true);
            const refreshedStation = refreshedResponse.data;
            let refreshedTracks = (refreshedStation as any).tracks || (refreshedStation as any).songs || [];
            
            if (refreshedTracks && typeof refreshedTracks === 'object' && 'data' in refreshedTracks && Array.isArray(refreshedTracks.data)) {
              console.log("[RadioStation] 📦 Extracting from FeedResourceCollection on retry...");
              refreshedTracks = refreshedTracks.data;
            }
            
            if (Array.isArray(refreshedTracks) && refreshedTracks.length > 0) {
              const firstRefreshedTrack = refreshedTracks[0];
              const isNowHydrated = firstRefreshedTrack && (
                firstRefreshedTrack.title || 
                firstRefreshedTrack.body || 
                firstRefreshedTrack.streamUrl || 
                (firstRefreshedTrack.audio && Array.isArray(firstRefreshedTrack.audio) && firstRefreshedTrack.audio.length > 0)
              );
              
              console.log(`[RadioStation] Retry ${retries + 1} result:`, {
                isNowHydrated,
                firstTrack: {
                  ulid: firstRefreshedTrack?.ulid,
                  hasTitle: !!firstRefreshedTrack?.title,
                  hasBody: !!firstRefreshedTrack?.body,
                  hasStreamUrl: !!firstRefreshedTrack?.streamUrl,
                  hasAudio: !!firstRefreshedTrack?.audio,
                  audioType: typeof firstRefreshedTrack?.audio,
                  audioIsArray: Array.isArray(firstRefreshedTrack?.audio),
                  keys: firstRefreshedTrack ? Object.keys(firstRefreshedTrack).slice(0, 15) : [],
                },
              });
              
              if (isNowHydrated) {
                console.log(`[RadioStation] ✅ Tracks hydrated after ${(retries + 1) * delayMs}ms`);
                stationTracks = refreshedTracks;
                break;
              } else {
                console.log(`[RadioStation] ⏳ Retry ${retries + 1}/${maxRetries} - tracks still not hydrated`);
              }
            } else {
              console.log(`[RadioStation] ⚠️  Retry ${retries + 1}: No tracks found in refreshed station`);
            }
            
            retries++;
          }
          
          if (retries >= maxRetries) {
            console.warn("[RadioStation] ⚠️  Tracks failed to hydrate after retries, may be feed items only");
          }
        } else {
          console.log("[RadioStation] ✅ Tracks are already hydrated (full post objects)");
        }
      }
      console.log("[RadioStation] ✅ Step 4 complete - Final tracks:", {
        count: stationTracks.length,
        firstTrack: stationTracks[0] ? {
          ulid: stationTracks[0].ulid,
          title: stationTracks[0].title,
          hasStreamUrl: !!stationTracks[0].streamUrl,
          hasAudio: !!stationTracks[0].audio,
        } : null,
      });
      
      if (!station || stationTracks.length === 0) {
        console.error("[RadioStation] ❌ No tracks found:", { hasStation: !!station, tracksCount: stationTracks.length });
        throw new Error("Radio station has no tracks");
      }

      console.log("[RadioStation] 🔍 Step 5: Filtering tracks (available + audio)...");

      // Debug: Log first few track states
      console.log("[RadioStation] First 3 tracks state check:", stationTracks.slice(0, 3).map((t: Post) => ({
        ulid: t.ulid,
        state: t.state,
        stateType: typeof t.state,
        isProcessing: (t as any).isProcessing,
        isDeleted: (t as any).isDeleted,
        deletedAt: t.deletedAt,
        hasAudio: !!(t as any).audio,
        audioLength: (t as any).audio?.length,
      })));

      // Filter out unavailable tracks
      // Note: API returns isProcessing (boolean) and isDeleted (boolean) instead of state/deletedAt
      const availableTracks = stationTracks.filter((track: Post) => {
        const isProcessing = (track as any).isProcessing === true;
        const isDeleted = (track as any).isDeleted === true || track.deletedAt;
        return !isProcessing && !isDeleted;
      });
      console.log("[RadioStation] Available tracks:", {
        total: stationTracks.length,
        available: availableTracks.length,
        filtered: stationTracks.length - availableTracks.length,
      });

      // Filter to tracks that have audio URLs
      const tracksWithAudio = availableTracks.filter((track: Post) => {
        // Check for audio URL in various possible locations
        // Cast to any to access dynamic properties that may exist on track
        const trackAny = track as any;
        const hasAudio =
          track.streamUrl ||
          (trackAny.audio && Array.isArray(trackAny.audio) && trackAny.audio.length > 0 && trackAny.audio[0]?.url) ||
          (trackAny.audios && Array.isArray(trackAny.audios) && trackAny.audios.length > 0 && trackAny.audios[0]?.url) ||
          (track.attachments && Array.isArray(track.attachments) && track.attachments.some((a: any) => a?.type === 'audio' && a?.url)) ||
          track.audioUrl ||
          track.audioSrc;

        // Log tracks without audio for debugging
        if (!hasAudio) {
          console.warn("[RadioStation] Track missing audio URL:", {
            ulid: track.ulid,
            title: track.title,
            hasStreamUrl: !!track.streamUrl,
            hasAudioArray: !!(trackAny.audio && Array.isArray(trackAny.audio) && trackAny.audio.length > 0),
            audioArrayLength: trackAny.audio ? (Array.isArray(trackAny.audio) ? trackAny.audio.length : 'not array') : 0,
            firstAudioUrl: trackAny.audio && Array.isArray(trackAny.audio) && trackAny.audio.length > 0 ? trackAny.audio[0]?.url : null,
            hasAudiosArray: !!(trackAny.audios && Array.isArray(trackAny.audios) && trackAny.audios.length > 0),
            hasAttachments: !!(track.attachments && Array.isArray(track.attachments)),
            hasAudioUrl: !!track.audioUrl,
            hasAudioSrc: !!track.audioSrc,
            trackKeys: Object.keys(track),
          });
        }

        return hasAudio;
      });

      // Find a sample track without audio for debugging (if any)
      const sampleWithoutAudio = availableTracks.find((t: Post) => !tracksWithAudio.includes(t));

      console.log("[RadioStation] ✅ Step 5 complete - Tracks with audio:", {
        total: stationTracks.length,
        available: availableTracks.length,
        withAudio: tracksWithAudio.length,
        firstTrack: tracksWithAudio[0] ? {
          ulid: tracksWithAudio[0].ulid,
          title: tracksWithAudio[0].title,
          audio: (tracksWithAudio[0] as any).audio,
          audios: (tracksWithAudio[0] as any).audios,
          attachments: tracksWithAudio[0].attachments,
          audioUrl: tracksWithAudio[0].audioUrl,
          streamUrl: tracksWithAudio[0].streamUrl,
        } : null,
        sampleTrackWithoutAudio: sampleWithoutAudio ? {
          ulid: sampleWithoutAudio.ulid,
          title: sampleWithoutAudio.title,
          audio: (sampleWithoutAudio as any).audio,
        } : null,
      });

      if (tracksWithAudio.length === 0) {
        const errorMsg = `Radio station has no tracks with audio files. Total tracks: ${stationTracks.length}, Available: ${availableTracks.length}`;
        console.error("[RadioStation] " + errorMsg, {
          stationTracks: stationTracks.slice(0, 3).map((t: Post) => ({
            ulid: t.ulid,
            title: t.title,
            state: t.state,
            deletedAt: t.deletedAt,
            audio: t.audio,
            audios: t.audios,
            attachments: t.attachments,
            audioUrl: t.audioUrl,
            streamUrl: t.streamUrl,
          })),
        });
        throw new Error(errorMsg);
      }

      // Create playback session using tracks with audio
      const session: RadioStationPlaybackSession = {
        stationUlid,
        startPosition: Math.min(startPosition, tracksWithAudio.length - 1),
        currentPosition: Math.min(startPosition, tracksWithAudio.length - 1),
        tracks: tracksWithAudio,
        startedAt: Date.now(),
        songsPlayed: 0,
      };

      radioSession.value = session;
      isRadioMode.value = true;

      // Set repeat mode to 'all' for continuous looping of the radio station
      audioPlayer.setRepeatMode("all");

      // Set up queue starting from random position
      // Note: setupRadioQueue calls audioPlayer.play() which resets radio mode,
      // so we must call setRadioStationMode(true) AFTER setupRadioQueue
      setupRadioQueue(session);

      // Enable radio station mode in audio player (disables seeking/skipping)
      // This must be called AFTER setupRadioQueue because play() resets radio mode
      // Pass the first track ID so the player can block navigating past it
      const firstTrackId = audioPlayer.queue.value[0]?.ulid || audioPlayer.queue.value[0]?.id;
      audioPlayer.setRadioStationMode(true, firstTrackId);
    } catch (error) {
      console.error("Failed to start radio station:", error);
      throw error;
    }
  }

  /**
   * Create queue for radio station starting from current position
   */
  function createRadioQueue(session: RadioStationPlaybackSession): Post[] {
    if (!session || session.tracks.length === 0) return [];

    // Create queue starting from current position, then wrapping around
    const queue: Post[] = [];
    const startIdx = session.currentPosition;

    // Add tracks from start position to end
    for (let i = startIdx; i < session.tracks.length; i++) {
      queue.push(session.tracks[i]);
    }

    // Add tracks from beginning to start position (for looping)
    for (let i = 0; i < startIdx; i++) {
      queue.push(session.tracks[i]);
    }

    return queue;
  }

  /**
   * Set up the audio player queue for radio station playback
   */
  function setupRadioQueue(session: RadioStationPlaybackSession) {
    const queue = createRadioQueue(session);
    
    console.log("[RadioStation] Setting up queue:", {
      queueLength: queue.length,
      firstTrack: queue[0] ? {
        ulid: queue[0].ulid,
        title: queue[0].title,
        audio: queue[0].audio,
        audios: queue[0].audios,
        attachments: queue[0].attachments,
      } : null,
    });
    
    // Set queue in audio player by playing the first track with the queue
    if (queue.length > 0) {
      const firstTrack = queue[0];
      console.log("[RadioStation] Calling audioPlayer.play()", {
        firstTrack: {
          ulid: firstTrack.ulid,
          title: firstTrack.title,
          audio: firstTrack.audio,
          audios: firstTrack.audios,
          attachments: firstTrack.attachments,
          audioUrl: firstTrack.audioUrl,
          streamUrl: firstTrack.streamUrl,
        },
        queueLength: queue.length,
      });
      
      try {
        audioPlayer.play(firstTrack, queue);
        console.log("[RadioStation] audioPlayer.play() called successfully", {
          currentTrack: audioPlayer.currentTrack.value ? {
            ulid: audioPlayer.currentTrack.value.ulid,
            title: audioPlayer.currentTrack.value.title,
          } : null,
        });
      } catch (error) {
        console.error("[RadioStation] Error calling audioPlayer.play():", error);
        throw error;
      }
    } else {
      console.error("[RadioStation] Cannot setup queue - empty queue");
      throw new Error("Radio station queue is empty");
    }
  }

  /**
   * Stop radio station playback
   */
  function stopRadioStation() {
    radioSession.value = null;
    isRadioMode.value = false;
    audioPlayer.setRadioStationMode(false);
    audioPlayer.stop();
  }

  /**
   * Handle track end - continue to next track or loop
   * The audio player's repeat mode 'all' handles looping automatically,
   * but we need to update our session tracking
   */
  function handleTrackEnd() {
    if (!radioSession.value || !isRadioMode.value) return;

    const session = radioSession.value;
    session.songsPlayed++;
    
    // The audio player handles looping via repeat mode 'all',
    // so we just need to track that we've played another song
    // Ads are handled automatically by the audio player (every 5 songs)
  }

  /**
   * Refresh playlist tracks (called when loop completes)
   */
  async function refreshPlaylistTracks() {
    if (!radioSession.value) return;

    const session = radioSession.value;
    
    try {
      // Fetch fresh playlist data using the service directly
      const { radioStationService } = await import("@/services/radioStationService");
      const station = await radioStationService.getRadioStation(session.stationUlid, true);

      // API returns tracks in 'tracks' field when includeTracks=true
      const stationTracks = (station as any).tracks || (station as any).songs || [];
      
      if (station && stationTracks.length > 0) {
        // Filter out unavailable tracks
        // Note: API returns isProcessing (boolean) and isDeleted (boolean) instead of state/deletedAt
        const availableTracks = stationTracks.filter((track: Post) => {
          const isProcessing = (track as any).isProcessing === true;
          const isDeleted = (track as any).isDeleted === true || track.deletedAt;
          return !isProcessing && !isDeleted;
        });

        if (availableTracks.length > 0) {
          // Update tracks but keep current position
          session.tracks = availableTracks;
          // Reset position if current position is out of bounds
          if (session.currentPosition >= availableTracks.length) {
            session.currentPosition = 0;
          }
          // Update queue with fresh tracks
          setupRadioQueue(session);
        }
      }
    } catch (error) {
      console.error("Failed to refresh playlist tracks:", error);
      // Continue with existing tracks on error
    }
  }

  /**
   * Skip unavailable track (called when track fails to load)
   */
  function skipUnavailableTrack() {
    if (!radioSession.value || !isRadioMode.value) return;

    const session = radioSession.value;
    session.currentPosition++;

    // If we've reached the end, loop back to start
    if (session.currentPosition >= session.tracks.length) {
      session.currentPosition = 0;
    }

    // Set up queue for next position
    setupRadioQueue(session);
  }

  // Watch for track changes to handle unavailable tracks
  // Track the last skipped ULID to prevent infinite loops
  let lastSkippedUlid: string | null = null;

  watch(
    () => audioPlayer.currentTrack.value,
    (track: Post | null) => {
      if (!isRadioMode.value || !radioSession.value) return;

      // Check if track is unavailable
      // Note: API returns isProcessing (boolean) and isDeleted (boolean) instead of state/deletedAt
      const isProcessing = (track as any)?.isProcessing === true;
      const isDeleted = (track as any)?.isDeleted === true || track?.deletedAt;

      if (track && (isProcessing || isDeleted)) {
        // Prevent infinite loop by checking if we just skipped this track
        if (track.ulid === lastSkippedUlid) {
          console.warn("[RadioStation] Already skipped this track, stopping to prevent infinite loop:", track.ulid);
          return;
        }
        lastSkippedUlid = track.ulid ?? null;
        console.warn("[RadioStation] Track unavailable, skipping:", track.ulid, { isProcessing, isDeleted });
        skipUnavailableTrack();
      } else if (track) {
        // Reset skip tracker on successful track
        lastSkippedUlid = null;
      }
    },
  );

  // Cleanup on unmount
  onUnmounted(() => {
    if (isRadioMode.value) {
      stopRadioStation();
    }
  });

  const instance: RadioStationComposable = {
    radioSession: computed(() => radioSession.value),
    isRadioMode: computed(() => isRadioMode.value),
    startRadioStation,
    stopRadioStation,
    skipUnavailableTrack,
  };

  // Store singleton instance
  radioStationInstance = instance;

  return instance;
}

// Export function to get singleton instance
export function getRadioStation() {
  return useRadioStation();
}
